<template>
  <article>
    <activity-stage
      :customRootContainerClass="`find-the-letter ${keyboardEnabled ? '' : 'full-height'}`"
    >
      <article id="instructions-container" slot="instructions">
        <audio-toggle-button></audio-toggle-button>
        <h1 class="find-the-letter-instructions">
          <span v-html="highlightedInstructionsText"></span>
        </h1>
      </article>
      <article slot="content">
        <section class="find-the-letters-container">
          <div class="letters-to-type-container" v-for="word of words" :key="word.id">
            <aside>
              <img
                :alt="word.wordImage.image.description"
                :src="word.wordImage.image.src"
                :class="`image word-${word.id}`"
              />
            </aside>

            <div class="letter-container">
              <div style="display: flex; flex-flow: row nowrap; justify-content: flex-start">
                <form>
                  <fieldset
                    :aria-label="word.letters.map(letter => letter.letter).join('')"
                    style="border: none; display: flex;"
                  >
                    <template v-for="(letter, index) in word.letters">
                      <div style="align-items: center; display: flex; flex-flow: column">
                        <label
                          :class="letter.classes"
                          :for="`${word.id}-${index}-${letter.letter}`"
                          class="letter"
                        >
                          <input
                            v-model="letterSelections"
                            :id="`${word.id}-${index}-${letter.letter}`"
                            :value="`${word.id}-${index}-${letter.letter}`"
                            @change="
                              handleLetterCheckboxNativeChange(
                                `${word.id}-${index}-${letter.letter}`,
                                letter
                              )
                            "
                            @keyup.enter="
                              handleLetterCheckboxKeyEnter(
                                `${word.id}-${index}-${letter.letter}`,
                                letter
                              )
                            "
                            tabindex="0"
                            type="checkbox"
                          />
                          <span
                            :class="letter.classes"
                            :id="`${word.id}-${index}-${letter.letter}-span`"
                          >
                            {{ letter.letter }}
                          </span>
                        </label>
                      </div>
                    </template>
                  </fieldset>
                </form>
              </div>
            </div>
          </div>
        </section>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';
import ActivityStage from 'components/activity/ActivityStage.vue';
import AudioToggleButton from 'components/buttons/AudioToggleButton';

export default {
  components: {
    ActivityStage,
    AudioToggleButton
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'Find The Letter',
      currentInstructions: 'Find and select every',
      words: [],
      letterSelections: []
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    letterColor() {
      const upperCase = this.currentRound?.letter?.toUpperCase();

      if (['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'].includes(upperCase)) return 'Yellow';
      if (['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'].includes(upperCase)) return 'Green';
      if (['Z', 'X', 'C', 'V', 'B', 'N', 'M'].includes(upperCase)) return 'Blue';

      return '';
    },

    foundAllLetters() {
      if (!this.words.length) return false;

      return !this.words.some(word =>
        word.letters.find(letter => letter.isTarget && !letter.classes.clicked)
      );
    },

    lettersToByTyped() {
      return this.words.reduce((agg, word) => {
        const targetLetters = word.letters.filter(letter => letter.isTarget && !letter.typed);
        return [...agg, ...targetLetters];
      }, []);
    },

    highlightedInstructionsText() {
      const text = this.instructions?.text || '';
      if (!text) return '';
      const lastCharIndex = text.length - 2;
      const lastChar = text[lastCharIndex];
      const before = text.slice(0, lastCharIndex);
      return `${before}<span class="color${this.letterColor}">${lastChar}</span>.`;
    }
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();

      this.createRounds(roundSetup);
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ steps }) => {
        steps.forEach(({ wordImage }) => {
          this.queueImage(wordImage.image.src);
        });
      });

      return this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;
      return {
        instructions: rounds[0]?.selectLetterInstructions,
        rounds: rounds.map(({ steps, ...round }) => ({
          ...round,
          steps: steps.map(step => ({
            ...step,
            answer: round.letter
          }))
        }))
      };
    },

    startActivity() {
      this.$nextTick(this.setActivityContainerHeight);

      const timeline = new TimelineLite();

      timeline
        .call(this.setNewRound)
        .call(this.hideKeyboard)
        .call(this.errorLoggerStart)
        .to('.activity-stage', 1, { opacity: 1 });
    },

    getKeyboardConfig() {
      const {
        keyboard: { characters, keyboardType, roofs }
      } = this.activityData;
      const isTouchHold = !!characters;
      const showTargets = this.isMobile && !(this.ipadWithoutExternalKeyboard && isTouchHold);

      return {
        showTargets,
        keyboardType,
        roofs
      };
    },

    getCurrentRoundState() {
      const targetLetter = this.currentRound.letter.toLowerCase();

      const result = this.currentRound.steps.map(({ id, word, wordImage }) => ({
        id,
        wordImage,
        letters: word.split('').map(letter => ({
          letter,
          classes: {
            clicked: false,
            [`color${this.letterColor}`]: false
          },
          isTarget: letter.toLowerCase() === targetLetter,
          typed: false
        }))
      }));

      if (this.foundAllLetters) {
        const firstWord = result[0];
        const firstLetterToBeTyped = firstWord.letters.find(letter => letter.isTarget);

        if (!firstLetterToBeTyped) return result;

        Object.assign(firstLetterToBeTyped.classes, {
          [`color${this.letterColor}`]: true
        });
      }

      return result;
    },

    setNewRound() {
      this.setupNewRound();

      this.setInstructions(this.currentRound.selectLetterInstructions);

      this.setScreenReaderAlert(`${this.currentRound.selectLetterInstructions.text}`);

      this.words = this.getCurrentRoundState();
      this.letterSelections = [];
    },

    handleLetterClick(letterObj) {
      if (letterObj.classes.clicked) return;

      if (letterObj.isTarget) {
        Object.assign(letterObj.classes, {
          clicked: true,
          [`color${this.letterColor}`]: true
        });

        this.sndBell();

        if (this.foundAllLetters) {
          TweenMax.delayedCall(1, () => {
            this.setUpTyping();
          });
        }
      } else {
        this.sndError();
      }
    },

    setUpTyping() {
      this.setInstructions(this.currentRound.typeLetterInstructions);
      this.setScreenReaderInstructions(this.currentRound.letter);
      this.setTotalKeyboardingSegment(this.currentRound.letter);

      this.enableKeyboard();
      this.showKeyboard({ enable: true });

      this.words = this.getCurrentRoundState();
    },

    moveSelectedLetter() {
      let foundTyped = false;
      let foundNext = false;

      this.words = this.words.map(word => {
        word.letters.forEach(letter => {
          if (letter.isTarget && !letter.typed) {
            if (!foundTyped) {
              letter.typed = true;
              letter.classes = {
                ...letter.classes,
                [`color${this.letterColor}`]: false
              };
              foundTyped = true;
            } else if (!foundNext) {
              letter.classes = {
                ...letter.classes,
                [`color${this.letterColor}`]: true
              };
              foundNext = true;
            }
          }
        });
        return word;
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.moveSelectedLetter();

      this.captureKeyboardAttemptSegment(attempt, true);

      if (!this.lettersToByTyped.length) {
        this.sndBell();
        this.hideKeyboard();
        this.handleRoundEnd();
      } else {
        this.setScreenReaderInstructions(this.currentRound.letter);
        this.setTotalKeyboardingSegment(this.currentRound.letter);
      }
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();

      if (this.isLastRound) {
        timeline.delay(1).call(this.completeActivity);
      } else {
        timeline
          .delay(1)
          .to('.activity-stage', 0.5, { opacity: 0 })
          .call(this.setNewRound)
          .call(this.$nextTick, [() => timeline.to('.activity-stage', 0.5, { opacity: 1 })]);
      }
    },

    handleLetterCheckboxNativeChange(letterSelector, letter) {
      const { isTarget } = letter;

      this.$nextTick(() => {
        if (!isTarget) {
          this.sndError();

          this.promptLetterSelectionErrorState(letterSelector);

          this.letterSelections = this.letterSelections.filter(
            selection => !selection.includes(letterSelector)
          );
          return;
        }

        if (!this.letterSelections.includes(letterSelector)) {
          this.letterSelections.push(letterSelector);
          return;
        }

        Object.assign(letter.classes, {
          clicked: true,
          [`color${this.letterColor}`]: true
        });

        this.sndBell();

        if (this.foundAllLetters) {
          TweenMax.delayedCall(1, () => {
            document.getElementById(letterSelector)?.blur();
            this.setUpTyping();
          });
        }
      });
    },

    handleLetterCheckboxKeyEnter(letterSelector, letter) {
      const { isTarget } = letter;

      if (!isTarget) {
        this.sndError();
        this.promptLetterSelectionErrorState(letterSelector);
        return;
      }

      if (!this.letterSelections.includes(letterSelector)) {
        this.letterSelections.push(letterSelector);

        Object.assign(letter.classes, {
          clicked: true,
          [`color${this.letterColor}`]: true
        });

        this.sndBell();

        if (this.foundAllLetters) {
          TweenMax.delayedCall(1, () => {
            document.getElementById(letterSelector)?.blur();
            this.setUpTyping();
          });
        }
      }
    },
    setScreenReaderInstructions(character) {
      let instructions;

      if (!character) {
        instructions = '';
      } else {
        const timeOrTimes = `time${this.lettersToByTyped.length > 1 ? 's' : ''}`;
        instructions = `Type the ${character} key ${this.lettersToByTyped.length} ${timeOrTimes}.`;
      }

      this.rerenderScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
    },

    promptLetterSelectionErrorState(selector) {
      const el = document.getElementById(`${selector}-span`);
      if (el) {
        el.classList.add('error');
        setTimeout(() => {
          el.classList.remove('error');
        }, 150);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.letter {
  position: 'relative';

  input {
    cursor: pointer;
    height: 0;
    opacity: 0;
    position: absolute;
    width: 0;

    &:focus ~ span {
      position: relative;

      &::after {
        content: '';
        outline: 5px auto #015fcc;
        width: 100%;
        height: 100%;
        position: absolute;
        left: 0px;
        top: 0px;
      }
    }
  }

  span.error {
    color: #ee0000;
    position: relative;

    &::after {
      content: '';
      outline: 1px dotted #ee0000 !important;
      outline: 5px auto #ee0000 !important;
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0px;
      top: 0px;
    }
  }
}
.find-the-letter-instructions {
  display: inline-block;
  margin: 0px 0px 0px 10px;

  span {
    padding: 3px;
  }
}

.find-the-letters-container {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;

  @media (max-width: 690px) {
    flex-flow: row wrap;
    justify-content: center;
  }
}

.letters-to-type-container {
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
}

.letter-container {
  display: flex;
  flex-flow: column;

  span {
    padding: 3px;
    cursor: pointer;
  }
}

.colorYellow {
  background-color: yellow;
}

.colorGreen {
  background-color: lightgreen;
}

.colorBlue {
  background-color: lightblue;
}

.solve-all-button {
  background-color: black;
  display: flex;
  justify-content: center;
  margin: 16px auto 0px auto;
  padding: 5px;
  border-radius: 6px;

  span {
    user-select: none;
    color: white;
  }
}

#instructions-container {
  position: relative;
  text-align: center;

  button {
    position: absolute;
  }

  h1 {
    left: 30px;
    position: relative;
    top: 5px;
  }
}

.tablet {
  .find-the-letters-container {
    width: calc(100% - 77px);
  }
}
</style>
